import { FadeIn } from "./common";
import CategoryHeader from "./CategoryHeader";
import { CategoryBackground } from "./SciBackground";
import { FlaskConical, ListChecks, Users, Bot, ShieldCheck } from "lucide-react";

const STYLES = [
  { icon: FlaskConical, t: "Scientific", d: "Evidence before conclusions." },
  { icon: ListChecks, t: "Systematic", d: "Break complex problems into testable components." },
  { icon: Users, t: "Cross-Functional", d: "Translate between Manufacturing, Quality, Engineering and technical teams." },
  { icon: Bot, t: "Automation-Minded", d: "Ask whether repetitive processes can be made more reliable and scalable." },
  { icon: ShieldCheck, t: "Compliance-Aware", d: "Build digital solutions with traceability, integrity and controlled change in mind." },
];

export default function About() {
  return (
    <section id="about" className="relative overflow-hidden scroll-mt-16" data-testid="about-section">
      <CategoryBackground variant="precision" accent="teal" accent2="cyan" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <CategoryHeader id="about" category="about" eyebrow="About" title="Poojan Patel" description="A cross-functional bridge between regulated manufacturing, Product Quality Systems, manufacturing analytics and automation." />
            <div className="mt-6 space-y-4 text-pale/85 text-base leading-relaxed" data-testid="about-narrative">
              <p>
                I work at the intersection of regulated manufacturing, Product Quality Systems, manufacturing analytics and automation. I streamline
                fragmented data sources, automate repetitive evidence-gathering workflows, strengthen data traceability and convert complex
                manufacturing signals into faster investigations, clearer process decisions and sustainable Quality-system improvements.
              </p>
              <p>
                My experience spans cGMP biotechnology manufacturing support, Product Quality investigations, deviation/CAPA/change-control
                workflows, technical documentation, validation and qualification activities, manufacturing data analysis and Python/API-based
                automation.
              </p>
              <p>
                The problems I take on are ones where evidence is fragmented across instruments, files, Quality records, materials or systems — and
                where better data architecture, scientific RCA and controlled change can materially improve process performance.
              </p>
              <p className="text-steel text-sm border-l-2 border-cyan/40 pl-4">
                My strength is connecting the scientific, manufacturing, Quality-system and digital sides of a problem rather than treating them as
                separate disciplines.
              </p>
            </div>
            <p className="font-mono text-xs text-cyan/80 mt-8 tracking-wide" data-testid="about-philosophy">
              Understand the process. Verify the evidence. Protect data integrity. Control the change. Automate what should not be manual.
            </p>
          </div>
          <div>
            <p className="overline-label">Working Style</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="working-style-cards">
              {STYLES.map((s, i) => {
                const Icon = s.icon;
                return (
                  <FadeIn key={s.t} delay={i * 0.06} className={i === 4 ? "sm:col-span-2" : ""}>
                    <div className="panel p-5 h-full hover:border-cyan/40 transition-colors" data-testid={`style-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <Icon className="w-5 h-5 text-cyan" aria-hidden="true" />
                      <p className="font-sans text-sm font-semibold text-paper mt-3 uppercase tracking-wide">{s.t}</p>
                      <p className="text-xs text-steel mt-2 leading-relaxed">{s.d}</p>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
